import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import { TeamMembersSection } from './TeamMembersSection';
import { UpsellModal } from '@/components/upsell/UpsellModal';
import { PLANS, PLAN_ORDER } from '@/config/plans';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Building2, Palette, Users, Bell, Save, Loader2, 
  Plus, X, Check, Mail, Plug, Send, Trash2, FileText,
  Clock, Zap, MailCheck, BellRing, RefreshCw, ChevronDown, ChevronUp, Eye,
  CreditCard, Gem, Lock, Sparkles, Phone, PhoneForwarded, MessageSquare
} from 'lucide-react';
import api from '@/lib/api';
import { billingAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export function SettingsPage() {
  const { workspace, updateWorkspace, user } = useAuth();
  const { t } = useLanguage();
  const { planId: currentPlanId, plan: currentPlan, isLocked } = useCurrentPlan();
  // Plan gates for the Email + Telephony sections (P6 — frontend visibility).
  // Backend already enforces these via require_feature(); the UI must also
  // hide the tabs and skip the API calls so Starter never sees the modules.
  const smtpLocked = isLocked('smtp_enabled');
  const smsLocked = isLocked('sms_enabled');
  const isPlatformAdmin = user?.role === 'admin';
  const [isLoading, setIsLoading] = useState(false);
  const [upsellOpenForPlan, setUpsellOpenForPlan] = useState(null);
  const [upgradingPid, setUpgradingPid] = useState(null);
  
  // Form states
  const [companyName, setCompanyName] = useState(workspace?.branding?.company_name || workspace?.name || '');
  const [phone, setPhone] = useState(workspace?.branding?.phone || '');
  const [email, setEmail] = useState(workspace?.branding?.email || '');
  const [address, setAddress] = useState(workspace?.branding?.address || '');
  const [website, setWebsite] = useState(workspace?.branding?.website || '');
  const [primaryColor, setPrimaryColor] = useState(workspace?.branding?.primary_color || '#F59E0B');

  // Primary domain (set-once-then-lock per workspace).
  const [primaryDomain, setPrimaryDomain] = useState(workspace?.primary_domain || '');
  const [savingDomain, setSavingDomain] = useState(false);
  const isDomainLocked = !!workspace?.primary_domain_locked_at;
  
  // Services & Cities
  const [services, setServices] = useState(workspace?.services || []);
  const [cities, setCities] = useState(workspace?.cities || []);
  const [newService, setNewService] = useState('');
  const [newCity, setNewCity] = useState('');

  // Email SMTP states
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [fromName, setFromName] = useState('');
  const [adminNotificationEmail, setAdminNotificationEmail] = useState('');
  const [autoNotifyAdmin, setAutoNotifyAdmin] = useState(true);
  const [autoConfirmLead, setAutoConfirmLead] = useState(false);
  const [smtpConnected, setSmtpConnected] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [brevoApiKey, setBrevoApiKey] = useState('');
  const [brevoConfigured, setBrevoConfigured] = useState(false);
  const [brevoReadable, setBrevoReadable] = useState(true);
  const [brevoKeySuffix, setBrevoKeySuffix] = useState('');
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [tplName, setTplName] = useState('');
  const [tplSubject, setTplSubject] = useState('');
  const [tplBody, setTplBody] = useState('');
  const [tplSaving, setTplSaving] = useState(false);

  // Auto email states
  const [autoConfig, setAutoConfig] = useState({
    confirm_prospect: true, confirm_delay: 'immediate',
    notify_admin: true, notify_delay: 'immediate',
    followup_24h: false, followup_72h: false,
    templates: {},
  });
  const [autoSaving, setAutoSaving] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editTplSubject, setEditTplSubject] = useState('');
  const [editTplBody, setEditTplBody] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const { language } = useLanguage();
  const tf = (fr, en) => language === 'fr' ? fr : en;

  // Telephony (Twilio) — Secrétaire virtuelle V1
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [twilioNumber, setTwilioNumber] = useState('');
  const [forwardNumber, setForwardNumber] = useState('');
  const [voiceMessage, setVoiceMessage] = useState('');
  const [missedCallSms, setMissedCallSms] = useState('');
  const [telSaving, setTelSaving] = useState(false);

  useEffect(() => {
    if (!smtpLocked) { loadSmtpConfig(); loadEmailTemplates(); loadAutoConfig(); }
    if (!smsLocked) { loadTelephony(); }
  }, [smtpLocked, smsLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSmtpConfig = async () => {
    try {
      const { data } = await api.get('/email/config');
      if (data.configured) {
        setSmtpHost(data.smtp_host);
        setSmtpPort(String(data.smtp_port));
        setSmtpUsername(data.smtp_username || '');
        setFromEmail(data.from_email);
        setFromName(data.from_name);
        setAdminNotificationEmail(data.admin_notification_email || '');
        setSmtpConnected(data.is_connected);
        setSmtpConfigured(true);
        setAutoNotifyAdmin(data.auto_notify_admin);
        setAutoConfirmLead(data.auto_confirm_lead);
        setBrevoConfigured(data.brevo_configured || false);
        // Backend signals brevo_readable=false when the encrypted key in DB
        // cannot be decrypted with the current ENCRYPTION_KEY (key rotated).
        setBrevoReadable(data.brevo_readable !== false);
        setBrevoKeySuffix(data.brevo_key_suffix || '');
      }
    } catch (e) { console.error('loadSmtpConfig failed:', e); /* not configured yet */ }
  };

  const loadEmailTemplates = async () => {
    try {
      const { data } = await api.get('/email/templates');
      setEmailTemplates(data.templates);
    } catch (e) { console.error('loadEmailTemplates failed:', e); }
  };

  const saveSmtp = async () => {
    setSmtpSaving(true);
    try {
      await api.put('/email/config', {
        smtp_host: smtpHost, smtp_port: parseInt(smtpPort),
        smtp_username: smtpUsername,
        from_email: fromEmail, from_name: fromName,
        admin_notification_email: adminNotificationEmail,
        smtp_password: smtpPassword || undefined,
        brevo_api_key: brevoApiKey || undefined,
        auto_notify_admin: autoNotifyAdmin,
        auto_confirm_lead: autoConfirmLead,
      });
      setSmtpConfigured(true);
      setSmtpPassword('');
      setBrevoApiKey('');
      // After a fresh save the key is definitely readable.
      setBrevoReadable(true);
      toast.success(tf('Configuration sauvegardee', 'Configuration saved'));
    } catch { toast.error(tf('Erreur', 'Error')); }
    finally { setSmtpSaving(false); }
  };

  const testSmtp = async () => {
    setSmtpTesting(true);
    try {
      const { data } = await api.post('/email/config/test', { to_email: fromEmail });
      if (data.success) {
        setSmtpConnected(true);
        toast.success(tf(`Email de test envoye a ${data.to_email}`, `Test email sent to ${data.to_email}`));
      } else {
        setSmtpConnected(false);
        toast.error(data.message || tf('Echec du test', 'Test failed'));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || tf('Erreur', 'Error'));
    } finally { setSmtpTesting(false); }
  };

  const saveTemplate = async () => {
    if (!tplName) return;
    setTplSaving(true);
    try {
      const { data } = await api.post('/email/templates', { name: tplName, subject: tplSubject, body: tplBody });
      setEmailTemplates(prev => [data, ...prev]);
      setShowNewTemplate(false);
      setTplName(''); setTplSubject(''); setTplBody('');
      toast.success(tf('Template cree', 'Template created'));
    } catch { toast.error(tf('Erreur', 'Error')); }
    finally { setTplSaving(false); }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/email/templates/${id}`);
      setEmailTemplates(prev => prev.filter(t => t.template_id !== id));
    } catch { toast.error(tf('Erreur', 'Error')); }
  };

  const loadAutoConfig = async () => {
    try {
      const { data } = await api.get('/email/auto-config');
      setAutoConfig(data);
    } catch (e) { console.error('loadAutoConfig failed:', e); /* not configured yet */ }
  };

  const saveAutoConfig = async (updates = {}) => {
    setAutoSaving(true);
    try {
      const payload = { ...autoConfig, ...updates };
      delete payload.templates;
      const { data } = await api.put('/email/auto-config', payload);
      setAutoConfig(data);
      toast.success(tf('Configuration sauvegardee', 'Configuration saved'));
    } catch { toast.error(tf('Erreur', 'Error')); }
    finally { setAutoSaving(false); }
  };

  const saveAutoTemplate = async () => {
    if (!editingTemplate) return;
    setAutoSaving(true);
    try {
      const { data } = await api.put('/email/auto-config', {
        ...autoConfig,
        templates: { [editingTemplate]: { subject: editTplSubject, body: editTplBody } }
      });
      setAutoConfig(data);
      setEditingTemplate(null);
      toast.success(tf('Template mis a jour', 'Template updated'));
    } catch { toast.error(tf('Erreur', 'Error')); }
    finally { setAutoSaving(false); }
  };

  const openTemplateEditor = (type) => {
    const tpl = autoConfig.templates?.[type];
    setEditingTemplate(type);
    setEditTplSubject(tpl?.subject || '');
    setEditTplBody(tpl?.body || '');
  };

  const templateTypeLabels = {
    confirm_prospect: tf('Confirmation de reception', 'Receipt confirmation'),
    notify_admin: tf('Notification nouveau lead', 'New lead notification'),
    followup_24h: tf('Suivi 24h', '24h follow-up'),
    followup_72h: tf('Suivi 72h', '72h follow-up'),
  };

  // Telephony (Twilio) load + save
  const loadTelephony = async () => {
    try {
      const { data } = await api.get('/workspaces/sms-config');
      const cfg = data?.sms_config || {};
      setSmsEnabled(!!cfg.sms_enabled);
      setTwilioNumber(cfg.twilio_phone_number || '');
      setForwardNumber(cfg.forward_to_number || '');
      setVoiceMessage(cfg.voice_message || '');
      setMissedCallSms(cfg.missed_call_sms || '');
    } catch (e) { console.error('loadTelephony failed:', e); /* not configured yet */ }
  };

  const isE164 = (n) => /^\+[1-9]\d{7,14}$/.test((n || '').trim());

  const saveTelephony = async () => {
    if (twilioNumber && !isE164(twilioNumber)) {
      toast.error(tf(
        'Numéro professionnel invalide. Format requis : +14501234567',
        'Invalid business number. Required format: +14501234567'
      ));
      return;
    }
    if (forwardNumber && !isE164(forwardNumber)) {
      toast.error(tf(
        'Cellulaire invalide. Format requis : +14501234567',
        'Invalid cell phone. Required format: +14501234567'
      ));
      return;
    }
    if (smsEnabled && (!twilioNumber || !forwardNumber)) {
      toast.error(tf(
        'Le numéro professionnel et votre cellulaire sont requis pour activer.',
        'Business number and your cell phone are required to enable.'
      ));
      return;
    }
    setTelSaving(true);
    try {
      await api.put('/workspaces/sms-config', {
        twilio_phone_number: twilioNumber.trim(),
        forward_to_number: forwardNumber.trim(),
        voice_message: voiceMessage,
        missed_call_sms: missedCallSms,
        sms_enabled: smsEnabled,
      });
      toast.success(tf('Téléphonie sauvegardée', 'Telephony saved'));
    } catch (e) {
      toast.error(e.response?.data?.detail || tf('Erreur', 'Error'));
    } finally {
      setTelSaving(false);
    }
  };

  const handleSaveDomain = async () => {
    const value = primaryDomain.trim().toLowerCase();
    if (!value) {
      toast.error(t('common.error'));
      return;
    }
    setSavingDomain(true);
    try {
      await updateWorkspace({ primary_domain: value });
      toast.success(language === 'fr'
        ? 'Domaine principal verrouille avec succes.'
        : 'Primary domain locked successfully.');
    } catch (err) {
      const det = err?.response?.data?.detail;
      const msg = (typeof det === 'object' && det?.message) ? det.message
        : (typeof det === 'string' ? det : (language === 'fr' ? 'Echec de l\'enregistrement.' : 'Save failed.'));
      toast.error(msg);
    } finally {
      setSavingDomain(false);
    }
  };

  const handleSaveBranding = async () => {
    setIsLoading(true);
    try {
      await updateWorkspace({
        name: companyName,
        branding: {
          company_name: companyName,
          phone,
          email,
          address,
          website,
          primary_color: primaryColor,
        },
      });
      toast.success(t('settings.settingsSaved'));
    } catch (err) {
      console.error('Error saving:', err);
      toast.error(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveServices = async () => {
    setIsLoading(true);
    try {
      await updateWorkspace({ services, cities });
      toast.success(t('settings.servicesUpdated'));
    } catch (err) {
      console.error('Error saving:', err);
      toast.error(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const addService = () => {
    if (newService && !services.includes(newService)) {
      setServices([...services, newService]);
      setNewService('');
    }
  };

  const removeService = (service) => {
    setServices(services.filter(s => s !== service));
  };

  const addCity = () => {
    if (newCity && !cities.includes(newCity)) {
      setCities([...cities, newCity]);
      setNewCity('');
    }
  };

  const removeCity = (city) => {
    setCities(cities.filter(c => c !== city));
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight">{t('settings.title')}</h1>
        <p className="text-muted-foreground mt-1">
          {t('settings.description')}
        </p>
      </div>

      <Tabs defaultValue="branding" className="space-y-6">
        <TabsList>
          <TabsTrigger value="branding">
            <Building2 className="h-4 w-4 mr-2" />
            {t('settings.company')}
          </TabsTrigger>
          <TabsTrigger value="services">
            <Palette className="h-4 w-4 mr-2" />
            {t('settings.servicesAndCities')}
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="h-4 w-4 mr-2" />
            {t('settings.team')}
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="settings-billing-tab">
            <CreditCard className="h-4 w-4 mr-2" />
            {tf('Abonnement', 'Billing')}
          </TabsTrigger>
          {!smtpLocked && (
            <TabsTrigger value="email" data-testid="settings-email-tab">
              <Mail className="h-4 w-4 mr-2" />
              Email
            </TabsTrigger>
          )}
          {!smsLocked && (
            <TabsTrigger value="telephony" data-testid="settings-telephony-tab">
              <Phone className="h-4 w-4 mr-2" />
              {tf('Téléphonie', 'Telephony')}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.companyInfo')}</CardTitle>
              <CardDescription>
                {t('settings.companyInfoDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="company-name">{t('settings.companyName')}</Label>
                  <Input
                    id="company-name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Ex: Drains Expert Montréal"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('settings.phone')}</Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="514-555-1234"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('settings.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contact@exemple.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">{t('settings.website')}</Label>
                  <Input
                    id="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://www.exemple.com"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">{t('settings.address')}</Label>
                  <Input
                    id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 rue Principale, Montréal, QC"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>{t('settings.primaryColor')}</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-16 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-32 font-mono"
                    placeholder="#F59E0B"
                  />
                  <div 
                    className="w-10 h-10 rounded-md border"
                    style={{ backgroundColor: primaryColor }}
                  />
                </div>
              </div>

              <Button onClick={handleSaveBranding} disabled={isLoading}>
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {t('common.save')}
              </Button>
            </CardContent>
          </Card>

          {/* Primary Domain — set-once-then-lock */}
          <Card data-testid="primary-domain-card">
            <CardHeader>
              <CardTitle>
                {language === 'fr' ? 'Domaine principal' : 'Primary domain'}
              </CardTitle>
              <CardDescription>
                {language === 'fr'
                  ? "Le domaine officiel de votre entreprise. Une fois enregistre, il est verrouille comme identite de votre espace. Contactez le support pour le modifier."
                  : 'Your business\'s official domain. Once saved, it is locked as your workspace identity. Contact support to change it.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="primary-domain">
                  {language === 'fr' ? 'Domaine' : 'Domain'}
                </Label>
                <Input
                  id="primary-domain"
                  value={primaryDomain}
                  onChange={(e) => setPrimaryDomain(e.target.value)}
                  placeholder="toitureabc.ca"
                  disabled={isDomainLocked}
                  readOnly={isDomainLocked}
                  data-testid="primary-domain-input"
                />
                {isDomainLocked ? (
                  <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                    {language === 'fr'
                      ? `Verrouille le ${(workspace?.primary_domain_locked_at || '').slice(0, 10)}. Contactez le support pour modifier.`
                      : `Locked on ${(workspace?.primary_domain_locked_at || '').slice(0, 10)}. Contact support to change.`}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr'
                      ? 'Format: toitureabc.ca (sans http:// ni /). Cette operation est definitive.'
                      : 'Format: toitureabc.ca (no http:// or /). This action is permanent.'}
                  </p>
                )}
              </div>
              {!isDomainLocked && (
                <Button
                  onClick={handleSaveDomain}
                  disabled={savingDomain || !primaryDomain.trim()}
                  data-testid="primary-domain-save-btn"
                >
                  {savingDomain ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {language === 'fr' ? 'Enregistrer et verrouiller' : 'Save and lock'}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services & Cities Tab */}
        <TabsContent value="services" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Services */}
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.servicesOffered')}</CardTitle>
                <CardDescription>
                  {t('settings.servicesDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    placeholder={t('settings.addService')}
                    onKeyDown={(e) => e.key === 'Enter' && addService()}
                  />
                  <Button variant="outline" size="icon" onClick={addService}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {services.map((service) => (
                    <Badge key={service} variant="secondary" className="pl-3 pr-1 py-1">
                      {service}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 ml-1"
                        onClick={() => removeService(service)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                  {services.length === 0 && (
                    <p className="text-sm text-muted-foreground">{t('settings.noServices')}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Cities */}
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.serviceAreas')}</CardTitle>
                <CardDescription>
                  {t('settings.areasDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newCity}
                    onChange={(e) => setNewCity(e.target.value)}
                    placeholder={t('settings.addCity')}
                    onKeyDown={(e) => e.key === 'Enter' && addCity()}
                  />
                  <Button variant="outline" size="icon" onClick={addCity}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {cities.map((city) => (
                    <Badge key={city} variant="secondary" className="pl-3 pr-1 py-1">
                      {city}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 ml-1"
                        onClick={() => removeCity(city)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                  {cities.length === 0 && (
                    <p className="text-sm text-muted-foreground">{t('settings.noCities')}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <Button onClick={handleSaveServices} disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('settings.saveServices')}
          </Button>
        </TabsContent>

        {/* Team Tab — sales representatives management. Workspace owner can
            create reps that show up in the lead "Représentant assigné" dropdown. */}
        <TabsContent value="team" className="space-y-6">
          <TeamMembersSection user={user} tf={tf} />
        </TabsContent>

        {/* Billing / Abonnement Tab (read-only for clients) */}
        <TabsContent value="billing" className="space-y-6">
          {/* Current plan summary */}
          <Card data-testid="billing-current-plan">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                {tf('Forfait actuel', 'Current plan')}
              </CardTitle>
              <CardDescription>
                {isPlatformAdmin
                  ? tf(
                      'En tant qu\'Admin plateforme, vous voyez le forfait reel du workspace ci-dessous.',
                      'As Platform Admin, you can see the actual workspace plan below.'
                    )
                  : tf(
                      'Votre forfait est gere par l\'equipe SpaceLeadX. Contactez-nous pour toute modification.',
                      'Your plan is managed by the SpaceLeadX team. Contact us for any change.'
                    )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow">
                    <Gem className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-bold text-xl" data-testid="billing-plan-name">{currentPlan.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {currentPlan.price_monthly === 0
                        ? tf('Gratuit', 'Free')
                        : `${currentPlan.price_monthly}$ ${currentPlan.currency}/mois`}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">
                    {tf('Prospects utilises', 'Leads used')}
                  </p>
                  <p className="font-mono text-sm">
                    {workspace?.leads_count || 0} / {workspace?.leads_limit ?? currentPlan.limits.max_leads}
                  </p>
                </div>
              </div>

              {!isPlatformAdmin && (
                <div className="flex items-start gap-2 rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">
                  <Lock className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                  <p>
                    {tf(
                      'La modification du forfait est reservee a l\'equipe SpaceLeadX. Vous pouvez demander une mise a niveau ci-dessous.',
                      'Plan changes are restricted to the SpaceLeadX team. You can request an upgrade below.'
                    )}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Plan comparison cards */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                {tf('Comparer les forfaits', 'Compare plans')}
              </CardTitle>
              <CardDescription>
                {tf(
                  'Decouvrez ce que chaque forfait debloque pour votre entreprise.',
                  'Discover what each plan unlocks for your business.'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {PLAN_ORDER.map((pid) => {
                  const p = PLANS[pid];
                  const isCurrent = pid === currentPlanId;
                  // Use the curated `display_features` (already i18n'd and
                  // marketing-tuned) instead of the raw feature flag keys.
                  // The previous `.slice(0, 6)` was truncating the 5
                  // Enterprise-exclusive features, making Enterprise look
                  // identical to Pro in this UI.
                  const displayLines = (p.display_features?.[language]) || p.display_features?.fr || [];
                  const hasAiMarketing = pid === 'pro' || pid === 'enterprise';
                  const cardCls = isCurrent
                    ? 'border-2 border-amber-400 bg-amber-50/60 dark:bg-amber-500/10 ring-1 ring-amber-400/40'
                    : 'border';
                  return (
                    <div
                      key={pid}
                      className={`rounded-xl p-5 space-y-4 ${cardCls}`}
                      data-testid={`plan-card-${pid}`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-lg font-black tracking-tight">{p.name}</span>
                          <div className="flex items-center gap-1.5">
                            {hasAiMarketing && (
                              <Badge
                                className="bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-600 hover:to-violet-600 text-white border-0 text-[10px]"
                                data-testid={`plan-ai-badge-${pid}`}
                              >
                                🔥 {tf('IA Marketing incluse', 'AI Marketing included')}
                              </Badge>
                            )}
                            {isCurrent && (
                              <Badge className="bg-amber-500 hover:bg-amber-600 text-white border-0">
                                {tf('Actuel', 'Current')}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">{p.tagline.fr}</p>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-black">{p.price_monthly}$</span>
                        <span className="text-sm text-muted-foreground">{p.currency} / mois</span>
                      </div>
                      <ul className="space-y-1.5 text-sm min-h-[140px]" data-testid={`plan-features-${pid}`}>
                        {displayLines.length === 0 ? (
                          <li className="text-muted-foreground italic">
                            {tf('Fonctionnalites de base', 'Base features only')}
                          </li>
                        ) : (
                          displayLines.map((label) => (
                            <li key={label} className="flex items-start gap-2">
                              <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                              <span>{label}</span>
                            </li>
                          ))
                        )}
                      </ul>
                      {isCurrent ? (
                        <Button
                          variant="outline"
                          className="w-full"
                          disabled
                          data-testid={`plan-current-btn-${pid}`}
                        >
                          {tf('Forfait actuel', 'Current plan')}
                        </Button>
                      ) : isPlatformAdmin ? (
                        <Button
                          variant="outline"
                          className="w-full"
                          disabled
                          data-testid={`plan-admin-info-btn-${pid}`}
                        >
                          {tf('Modifier via /admin', 'Change via /admin')}
                        </Button>
                      ) : (
                        <Button
                          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                          disabled={upgradingPid === pid}
                          onClick={async () => {
                            // Enterprise self-serve checkout is intentionally
                            // gated via the existing UpsellModal (no monthly
                            // self-serve flow for enterprise yet).
                            if (pid === 'enterprise') {
                              setUpsellOpenForPlan(pid);
                              return;
                            }
                            setUpgradingPid(pid);
                            try {
                              const { data } = await billingAPI.createCheckout({ plan_id: pid, interval: 'monthly' });
                              if (data && data.checkout_url) {
                                window.location.href = data.checkout_url;
                                return;
                              }
                              toast.error(tf('Erreur paiement.', 'Payment error.'));
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || tf('Erreur paiement.', 'Payment error.'));
                            } finally {
                              setUpgradingPid(null);
                            }
                          }}
                          data-testid={`plan-upgrade-btn-${pid}`}
                        >
                          <Gem className="mr-2 h-4 w-4" />
                          {upgradingPid === pid
                            ? tf('Chargement…', 'Loading…')
                            : tf(`Passer a ${p.name}`, `Upgrade to ${p.name}`)}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Upsell modal driven by the upgrade buttons.
              We map a target plan -> a representative feature it unlocks so the
              modal can render its standard layout. */}
          <UpsellModal
            open={!!upsellOpenForPlan}
            onOpenChange={(o) => !o && setUpsellOpenForPlan(null)}
            feature={
              upsellOpenForPlan === 'enterprise'
                ? 'ai_agents'
                : upsellOpenForPlan === 'pro'
                ? 'seo_articles'
                : null
            }
          />
        </TabsContent>

        {/* Email SMTP Tab */}
        {!smtpLocked && (
        <TabsContent value="email" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Plug className="h-5 w-5" />
                    {tf('Configuration SMTP', 'SMTP Configuration')}
                  </CardTitle>
                  <CardDescription>{tf('Configurez votre serveur email pour envoyer des emails depuis SpaceLeadX', 'Configure your email server to send emails from SpaceLeadX')}</CardDescription>
                </div>
                {smtpConfigured && (
                  <Badge variant={smtpConnected ? 'default' : 'destructive'} className={smtpConnected ? 'bg-green-500/10 text-green-600' : ''}>
                    {smtpConnected ? tf('Connecte', 'Connected') : tf('Deconnecte', 'Disconnected')}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{tf('Serveur SMTP', 'SMTP Server')}</Label>
                  <Input value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="smtp.gmail.com" data-testid="smtp-host" />
                </div>
                <div className="space-y-1.5">
                  <Label>Port</Label>
                  <Select value={smtpPort} onValueChange={setSmtpPort}>
                    <SelectTrigger data-testid="smtp-port"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="587">587 (TLS)</SelectItem>
                      <SelectItem value="465">465 (SSL)</SelectItem>
                      <SelectItem value="25">25 (No encryption)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{tf('Email expediteur', 'Sender email')}</Label>
                  <Input value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="contact@monentreprise.ca" data-testid="smtp-email" />
                </div>
                <div className="space-y-1.5">
                  <Label>{tf('Identifiant SMTP (login)', 'SMTP username (login)')}</Label>
                  <Input
                    value={smtpUsername}
                    onChange={e => setSmtpUsername(e.target.value)}
                    placeholder={tf('Souvent identique à l\'email expediteur', "Often same as the sender email")}
                    data-testid="smtp-username"
                  />
                  <p className="text-xs text-muted-foreground">
                    {tf(
                      'Pour Brevo SMTP, Mailgun, SES : le login peut différer de l\'email expediteur. Laissez vide pour Gmail/Outlook.',
                      'For Brevo SMTP, Mailgun, SES: the login may differ from the sender email. Leave empty for Gmail/Outlook.'
                    )}
                  </p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{tf('Mot de passe / App password', 'Password / App password')}</Label>
                <Input
                  type="password"
                  value={smtpPassword}
                  onChange={e => setSmtpPassword(e.target.value)}
                  placeholder={smtpConfigured ? tf('Laissez vide pour conserver le mot de passe enregistré', 'Leave empty to keep the saved password') : ''}
                  data-testid="smtp-password"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{tf('Nom affiche', 'Display name')}</Label>
                <Input value={fromName} onChange={e => setFromName(e.target.value)} placeholder={tf('Ex: Pave Uni Reparation', 'Ex: Paving Repair Co')} data-testid="smtp-from-name" />
              </div>

              <div className="space-y-1.5">
                <Label>{tf('Email de notification admin (optionnel)', 'Admin notification email (optional)')}</Label>
                <Input
                  type="email"
                  value={adminNotificationEmail}
                  onChange={e => setAdminNotificationEmail(e.target.value)}
                  placeholder={fromEmail || tf("Laissez vide pour utiliser l'email expediteur", 'Leave empty to use the sender email')}
                  data-testid="smtp-admin-notification-email"
                />
                <p className="text-xs text-muted-foreground">
                  {tf(
                    "Adresse qui reçoit les notifications de nouveaux prospects. Si vide, l'email expediteur est utilisé.",
                    'Inbox that receives new-lead notifications. If empty, the sender email is used.'
                  )}
                </p>
              </div>

              <Separator />

              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label>{tf('Cle API Brevo (optionnel)', 'Brevo API Key (optional)')}</Label>
                  {brevoConfigured && brevoReadable && (
                    <Badge className="bg-green-500/10 text-green-600" data-testid="brevo-configured-badge">
                      {brevoKeySuffix
                        ? tf(`Configurée : ••••••••${brevoKeySuffix}`, `Configured: ••••••••${brevoKeySuffix}`)
                        : tf('Configure', 'Configured')}
                    </Badge>
                  )}
                  {brevoConfigured && !brevoReadable && (
                    <Badge className="bg-red-500/10 text-red-600" data-testid="brevo-unreadable-badge">
                      {tf('Clé illisible — à ressaisir', 'Key unreadable — please re-enter')}
                    </Badge>
                  )}
                </div>
                {brevoConfigured && !brevoReadable && (
                  <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-600 dark:text-red-400">
                    {tf(
                      "La clé Brevo enregistrée ne peut plus être déchiffrée (la clé de chiffrement du serveur a changé). Recollez votre clé Brevo ci-dessous et enregistrez à nouveau.",
                      'The saved Brevo key cannot be decrypted anymore (server encryption key changed). Paste your Brevo key below and save again.'
                    )}
                  </div>
                )}
                <Input
                  type="password"
                  value={brevoApiKey}
                  onChange={e => setBrevoApiKey(e.target.value)}
                  placeholder={
                    brevoConfigured && brevoReadable
                      ? tf('Laissez vide pour conserver la clé enregistrée', 'Leave empty to keep the saved key')
                      : 'xkeysib-...'
                  }
                  data-testid="brevo-api-key"
                />
                <p className="text-xs text-muted-foreground">
                  {tf('Disponible dans votre dashboard Brevo > SMTP & API > Cles API', 'Available in your Brevo dashboard > SMTP & API > API Keys')}
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={saveSmtp} disabled={smtpSaving || !smtpHost || !fromEmail} data-testid="save-smtp-btn">
                  {smtpSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  {tf('Sauvegarder', 'Save')}
                </Button>
                {smtpConfigured && (
                  <Button variant="outline" onClick={testSmtp} disabled={smtpTesting} data-testid="test-smtp-btn">
                    {smtpTesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                    {tf('Tester la connexion', 'Test connection')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* AUTO EMAILS SECTION */}
          <Card>
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  {tf('Emails automatiques', 'Automated emails')}
                </CardTitle>
                <CardDescription>{tf('Configurez les emails envoyes automatiquement lors des actions des prospects', 'Configure emails sent automatically on prospect actions')}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Confirmation prospect */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MailCheck className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-sm">{tf('Confirmation au prospect', 'Prospect confirmation')}</p>
                      <p className="text-xs text-muted-foreground">{tf('Email envoye au prospect quand il soumet un formulaire', 'Email sent to prospect when they submit a form')}</p>
                    </div>
                  </div>
                  <Switch
                    checked={autoConfig.confirm_prospect}
                    onCheckedChange={(v) => { setAutoConfig(prev => ({...prev, confirm_prospect: v})); saveAutoConfig({confirm_prospect: v}); }}
                    data-testid="auto-confirm-prospect-toggle"
                  />
                </div>
                {autoConfig.confirm_prospect && (
                  <div className="ml-8 space-y-3">
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <Label className="text-sm">{tf('Delai d\'envoi', 'Send delay')}</Label>
                      <Select value={autoConfig.confirm_delay} onValueChange={(v) => { setAutoConfig(prev => ({...prev, confirm_delay: v})); saveAutoConfig({confirm_delay: v}); }}>
                        <SelectTrigger className="w-40" data-testid="confirm-delay-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">{tf('Immediat', 'Immediate')}</SelectItem>
                          <SelectItem value="5min">5 min</SelectItem>
                          <SelectItem value="15min">15 min</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openTemplateEditor('confirm_prospect')} data-testid="edit-confirm-tpl-btn">
                      <FileText className="h-4 w-4 mr-2" />{tf('Personnaliser le template', 'Customize template')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Notification admin */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <BellRing className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-sm">{tf('Notification admin', 'Admin notification')}</p>
                      <p className="text-xs text-muted-foreground">{tf('Notification par email a l\'admin pour chaque nouveau prospect', 'Email notification to admin for each new lead')}</p>
                    </div>
                  </div>
                  <Switch
                    checked={autoConfig.notify_admin}
                    onCheckedChange={(v) => { setAutoConfig(prev => ({...prev, notify_admin: v})); saveAutoConfig({notify_admin: v}); }}
                    data-testid="auto-notify-admin-toggle"
                  />
                </div>
                {autoConfig.notify_admin && (
                  <div className="ml-8 space-y-3">
                    <div className="flex items-center gap-3">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <Label className="text-sm">{tf('Delai d\'envoi', 'Send delay')}</Label>
                      <Select value={autoConfig.notify_delay} onValueChange={(v) => { setAutoConfig(prev => ({...prev, notify_delay: v})); saveAutoConfig({notify_delay: v}); }}>
                        <SelectTrigger className="w-40" data-testid="notify-delay-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">{tf('Immediat', 'Immediate')}</SelectItem>
                          <SelectItem value="5min">5 min</SelectItem>
                          <SelectItem value="15min">15 min</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openTemplateEditor('notify_admin')} data-testid="edit-notify-tpl-btn">
                      <FileText className="h-4 w-4 mr-2" />{tf('Personnaliser le template', 'Customize template')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Follow-up 24h */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-sm">{tf('Relance automatique 24h', 'Auto follow-up 24h')}</p>
                      <p className="text-xs text-muted-foreground">{tf('Si le prospect est encore "Nouveau" apres 24h, envoyer un email de suivi', 'If lead is still "New" after 24h, send a follow-up email')}</p>
                    </div>
                  </div>
                  <Switch
                    checked={autoConfig.followup_24h}
                    onCheckedChange={(v) => { setAutoConfig(prev => ({...prev, followup_24h: v})); saveAutoConfig({followup_24h: v}); }}
                    data-testid="auto-followup-24h-toggle"
                  />
                </div>
                {autoConfig.followup_24h && (
                  <div className="ml-8">
                    <Button variant="outline" size="sm" onClick={() => openTemplateEditor('followup_24h')} data-testid="edit-followup24-tpl-btn">
                      <FileText className="h-4 w-4 mr-2" />{tf('Personnaliser le template', 'Customize template')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Follow-up 72h */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="h-5 w-5 text-red-600" />
                    <div>
                      <p className="font-medium text-sm">{tf('Relance automatique 72h', 'Auto follow-up 72h')}</p>
                      <p className="text-xs text-muted-foreground">{tf('Si le prospect est encore "Nouveau" apres 72h, envoyer une relance', 'If lead is still "New" after 72h, send a follow-up')}</p>
                    </div>
                  </div>
                  <Switch
                    checked={autoConfig.followup_72h}
                    onCheckedChange={(v) => { setAutoConfig(prev => ({...prev, followup_72h: v})); saveAutoConfig({followup_72h: v}); }}
                    data-testid="auto-followup-72h-toggle"
                  />
                </div>
                {autoConfig.followup_72h && (
                  <div className="ml-8">
                    <Button variant="outline" size="sm" onClick={() => openTemplateEditor('followup_72h')} data-testid="edit-followup72-tpl-btn">
                      <FileText className="h-4 w-4 mr-2" />{tf('Personnaliser le template', 'Customize template')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Variable reference */}
              <div className="bg-muted/40 rounded-lg p-3 text-xs text-muted-foreground">
                <p className="font-medium mb-1">{tf('Variables disponibles', 'Available variables')}:</p>
                <p className="font-mono">{'{{prenom}}, {{nom}}, {{email}}, {{telephone}}, {{ville}}, {{service}}, {{source}}, {{nom_entreprise}}, {{telephone_entreprise}}, {{lien_crm}}'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Template Editor Dialog */}
          <Dialog open={!!editingTemplate} onOpenChange={(open) => { if (!open) setEditingTemplate(null); }}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {editingTemplate && templateTypeLabels[editingTemplate]}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>{tf('Objet', 'Subject')}</Label>
                  <Input value={editTplSubject} onChange={e => setEditTplSubject(e.target.value)} data-testid="auto-tpl-subject" />
                </div>
                <div className="space-y-1.5">
                  <Label>{tf('Corps du message (HTML)', 'Message body (HTML)')}</Label>
                  <Textarea value={editTplBody} onChange={e => setEditTplBody(e.target.value)} rows={12} className="font-mono text-xs" data-testid="auto-tpl-body" />
                </div>
                {previewTemplate === editingTemplate ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">{tf('Apercu', 'Preview')}</Label>
                      <Button variant="ghost" size="sm" onClick={() => setPreviewTemplate(null)}>
                        <ChevronUp className="h-4 w-4 mr-1" />{tf('Masquer', 'Hide')}
                      </Button>
                    </div>
                    <div className="border rounded-lg p-4 bg-white max-h-60 overflow-y-auto" dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(editTplBody
                        .replace(/\{\{prenom\}\}/g, 'Jean')
                        .replace(/\{\{nom\}\}/g, 'Tremblay')
                        .replace(/\{\{email\}\}/g, 'jean@exemple.com')
                        .replace(/\{\{telephone\}\}/g, '514-555-1234')
                        .replace(/\{\{ville\}\}/g, 'Montreal')
                        .replace(/\{\{service\}\}/g, 'Plomberie')
                        .replace(/\{\{source\}\}/g, 'Formulaire web')
                        .replace(/\{\{nom_entreprise\}\}/g, workspace?.branding?.company_name || workspace?.name || 'Mon Entreprise')
                        .replace(/\{\{telephone_entreprise\}\}/g, workspace?.branding?.phone || '514-000-0000')
                        .replace(/\{\{lien_crm\}\}/g, '#'))
                    }} />
                  </div>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setPreviewTemplate(editingTemplate)}>
                    <Eye className="h-4 w-4 mr-2" />{tf('Apercu avec donnees fictives', 'Preview with sample data')}
                  </Button>
                )}
                <div className="bg-muted/40 rounded p-2 text-xs text-muted-foreground font-mono">
                  {'{{prenom}}, {{nom}}, {{email}}, {{telephone}}, {{ville}}, {{service}}, {{source}}, {{nom_entreprise}}, {{telephone_entreprise}}, {{lien_crm}}'}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingTemplate(null)}>{tf('Annuler', 'Cancel')}</Button>
                <Button onClick={saveAutoTemplate} disabled={autoSaving} data-testid="save-auto-tpl-btn">
                  {autoSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {tf('Sauvegarder', 'Save')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Manual Email Templates */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {tf('Templates d\'emails', 'Email templates')}
                  </CardTitle>
                  <CardDescription>{tf('Creez des templates reutilisables avec variables dynamiques', 'Create reusable templates with dynamic variables')}</CardDescription>
                </div>
                <Button size="sm" onClick={() => setShowNewTemplate(true)} data-testid="new-template-btn">
                  <Plus className="mr-2 h-4 w-4" />{tf('Nouveau', 'New')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {emailTemplates.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{tf('Aucun template', 'No templates')}</p>
              ) : (
                <div className="space-y-2">
                  {emailTemplates.map(tpl => (
                    <div key={tpl.template_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{tpl.name}</p>
                        <p className="text-xs text-muted-foreground truncate max-w-[400px]">{tpl.subject}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteTemplate(tpl.template_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* New Template Dialog */}
          <Dialog open={showNewTemplate} onOpenChange={setShowNewTemplate}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{tf('Nouveau template', 'New template')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>{tf('Nom du template', 'Template name')}</Label>
                  <Input value={tplName} onChange={e => setTplName(e.target.value)} placeholder={tf('Ex: Suivi de soumission', 'Ex: Quote follow-up')} data-testid="tpl-name" />
                </div>
                <div className="space-y-1.5">
                  <Label>{tf('Objet', 'Subject')}</Label>
                  <Input value={tplSubject} onChange={e => setTplSubject(e.target.value)} placeholder={tf('Objet de l\'email', 'Email subject')} data-testid="tpl-subject" />
                </div>
                <div className="space-y-1.5">
                  <Label>{tf('Corps du message', 'Message body')}</Label>
                  <Textarea value={tplBody} onChange={e => setTplBody(e.target.value)} rows={6} placeholder={tf('Bonjour {{prenom}},\n\nMerci...', 'Hello {{prenom}},\n\nThank you...')} data-testid="tpl-body" />
                  <p className="text-xs text-muted-foreground">{tf('Variables', 'Variables')}: {'{{prenom}}, {{nom}}, {{ville}}, {{service}}, {{nom_entreprise}}, {{telephone}}'}</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewTemplate(false)}>{tf('Annuler', 'Cancel')}</Button>
                <Button onClick={saveTemplate} disabled={!tplName || tplSaving}>
                  {tplSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {tf('Creer', 'Create')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        {/* Telephony Tab — Secrétaire virtuelle V1 */}
        {!smsLocked && (
        <TabsContent value="telephony" className="space-y-6" data-testid="settings-telephony-content">
          {/* How it works — terrain-friendly */}
          <Card className="border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
                  <Sparkles className="h-5 w-5 text-amber-500" />
                </div>
                <div className="space-y-3">
                  <h3 className="font-heading text-lg font-bold tracking-tight">
                    {tf('Comment ça fonctionne', 'How it works')}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {tf(
                      "Pendant que vous travaillez, SpaceLeadX répond automatiquement à vos appels et relance les clients quand vous êtes occupé.",
                      "While you work, SpaceLeadX answers your calls automatically and follows up with clients when you're busy."
                    )}
                  </p>
                  <ol className="space-y-2 text-sm">
                    <li className="flex items-center gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-xs font-bold text-amber-500">1</span>
                      <span>{tf('Un client appelle votre numéro professionnel', 'A client calls your business number')}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-xs font-bold text-amber-500">2</span>
                      <span>{tf('SpaceLeadX répond avec votre message d\'accueil', 'SpaceLeadX answers with your greeting')}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-xs font-bold text-amber-500">3</span>
                      <span>{tf("L'appel est transféré vers votre cellulaire", 'The call is transferred to your cell phone')}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-xs font-bold text-amber-500">4</span>
                      <span>{tf("Si vous ne répondez pas : un SMS automatique est envoyé et un prospect est créé", 'If you miss the call: an automatic SMS is sent and a lead is created')}</span>
                    </li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configuration */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    {tf('Secrétaire virtuelle', 'Virtual receptionist')}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {tf(
                      'Activez le service pour ne plus jamais manquer un client.',
                      'Turn on the service so you never miss a client again.'
                    )}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Label htmlFor="tel-enabled" className="text-sm font-medium">
                    {smsEnabled ? tf('Activé', 'On') : tf('Désactivé', 'Off')}
                  </Label>
                  <Switch
                    id="tel-enabled"
                    checked={smsEnabled}
                    onCheckedChange={setSmsEnabled}
                    data-testid="telephony-enabled-switch"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Numéro pro */}
              <div className="space-y-2">
                <Label htmlFor="tel-twilio" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  {tf('Numéro professionnel', 'Business number')}
                </Label>
                <Input
                  id="tel-twilio"
                  type="tel"
                  inputMode="tel"
                  placeholder="+14501234567"
                  value={twilioNumber}
                  onChange={(e) => setTwilioNumber(e.target.value)}
                  data-testid="telephony-twilio-number-input"
                />
                <p className="text-xs text-muted-foreground">
                  {tf(
                    'Le numéro que vos clients composent. Format : +1 puis 10 chiffres.',
                    'The number your clients dial. Format: +1 then 10 digits.'
                  )}
                </p>
              </div>

              {/* Cellulaire */}
              <div className="space-y-2">
                <Label htmlFor="tel-forward" className="flex items-center gap-2">
                  <PhoneForwarded className="h-4 w-4 text-muted-foreground" />
                  {tf('Votre cellulaire', 'Your cell phone')}
                </Label>
                <Input
                  id="tel-forward"
                  type="tel"
                  inputMode="tel"
                  placeholder="+15145551234"
                  value={forwardNumber}
                  onChange={(e) => setForwardNumber(e.target.value)}
                  data-testid="telephony-forward-number-input"
                />
                <p className="text-xs text-muted-foreground">
                  {tf(
                    "Le numéro où les appels seront transférés. C'est votre cellulaire personnel.",
                    'The number where calls will be forwarded. Usually your personal cell.'
                  )}
                </p>
              </div>

              {/* Message d'accueil */}
              <div className="space-y-2">
                <Label htmlFor="tel-greeting">
                  {tf("Message d'accueil", 'Greeting message')}
                </Label>
                <Textarea
                  id="tel-greeting"
                  rows={3}
                  maxLength={500}
                  placeholder={tf(
                    'Bonjour, merci d\'avoir appelé. Je vous transfère immédiatement.',
                    'Hello, thanks for calling. Transferring you now.'
                  )}
                  value={voiceMessage}
                  onChange={(e) => setVoiceMessage(e.target.value)}
                  data-testid="telephony-voice-message-input"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{tf('Ce que le client entend avant le transfert.', 'What the client hears before the transfer.')}</span>
                  <span>{voiceMessage.length}/500</span>
                </div>
              </div>

              {/* SMS appel manqué */}
              <div className="space-y-2">
                <Label htmlFor="tel-sms" className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  {tf('SMS si appel manqué', 'SMS if missed call')}
                </Label>
                <Textarea
                  id="tel-sms"
                  rows={3}
                  maxLength={320}
                  placeholder={tf(
                    'Bonjour, désolé j\'étais occupé. Rappelez-moi ou répondez à ce SMS, je vous reviens rapidement.',
                    'Hi, sorry I was busy. Call me back or reply to this SMS, I\'ll get back to you shortly.'
                  )}
                  value={missedCallSms}
                  onChange={(e) => setMissedCallSms(e.target.value)}
                  data-testid="telephony-missed-sms-input"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{tf('Envoyé automatiquement au client quand vous ne répondez pas.', 'Sent automatically to the client when you miss the call.')}</span>
                  <span>{missedCallSms.length}/320</span>
                </div>
              </div>

              <Separator />

              <div className="flex justify-end">
                <Button
                  onClick={saveTelephony}
                  disabled={telSaving}
                  data-testid="telephony-save-button"
                >
                  {telSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  {tf('Enregistrer', 'Save')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
